import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Chip, Stack, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import {
  Warning as WarningIcon, Info as InfoIcon, Build as ProcessIcon,
  CheckCircle as ConfirmIcon, Block as DismissIcon, Close as CloseIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const STATUS = {
  open:       { label: 'Открыт',     color: 'warning' },
  in_review:  { label: 'На разборе', color: 'info' },
  resolved:   { label: 'Решён',      color: 'success' },
  dismissed:  { label: 'Отклонён',   color: 'default' },
};
const SEVERITY = { low: 'default', medium: 'warning', high: 'error' };
const SEVERITY_T = { low: 'низкая', medium: 'средняя', high: 'высокая' };
const SOURCE_T = { process: 'мимо заявки', reality: 'расхождение с реальностью' };

const TABS = [
  { key: 'open', label: 'Открытые' },
  { key: 'in_review', label: 'На разборе' },
  { key: '', label: 'Все' },
  { key: 'resolved', label: 'Решённые' },
];

function Drift() {
  const { user } = useAuth();
  const can = (p) => user?.is_superuser || user?.permissions?.includes(p);

  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get('/drift/', { params: { status_filter: TABS[tab].key || undefined } });
      setRows(res.data.items || []);
      setOpenCount(res.data.open_count || 0);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const act = async (ev, action, body) => {
    setError('');
    try {
      await axios.post(`/drift/${ev.id}/${action}`, body || {});
      await load();
      if (detail && detail.id === ev.id) {
        const fresh = await axios.get(`/drift/${ev.id}`);
        setDetail(fresh.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const doProcess = (ev) => {
    const reason = window.prompt('Причина / объяснение изменения (обязательно):', '');
    if (reason && reason.trim()) act(ev, 'process', { reason: reason.trim() });
  };
  const doConfirm = (ev) => {
    const resolution = window.prompt('Комментарий руководителя (необязательно):', 'Подтверждаю');
    if (resolution !== null) act(ev, 'confirm', { resolution });
  };
  const doDismiss = (ev) => {
    const reason = window.prompt('Причина отклонения (ложное срабатывание и т.п.):', '');
    if (reason && reason.trim()) act(ev, 'dismiss', { reason: reason.trim() });
  };

  const rowActions = (ev) => {
    const btns = [];
    btns.push(<Tooltip key="v" title="Подробнее"><IconButton size="small" onClick={() => openDetail(ev)}><ViewIcon fontSize="small" /></IconButton></Tooltip>);
    if (ev.status === 'open' && can('drift.process'))
      btns.push(<Tooltip key="p" title="Обработать (указать причину)"><IconButton size="small" color="primary" onClick={() => doProcess(ev)}><ProcessIcon fontSize="small" /></IconButton></Tooltip>);
    if (ev.status === 'in_review' && can('drift.confirm')) {
      btns.push(<Tooltip key="c" title="Подтвердить"><IconButton size="small" color="success" onClick={() => doConfirm(ev)}><ConfirmIcon fontSize="small" /></IconButton></Tooltip>);
    }
    if (['open', 'in_review'].includes(ev.status) && can('drift.confirm'))
      btns.push(<Tooltip key="d" title="Отклонить (ложное)"><IconButton size="small" color="error" onClick={() => doDismiss(ev)}><DismissIcon fontSize="small" /></IconButton></Tooltip>);
    return <Stack direction="row">{btns}</Stack>;
  };

  const openDetail = async (ev) => {
    try {
      const res = await axios.get(`/drift/${ev.id}`);
      setDetail(res.data);
    } catch (err) { setError(err.response?.data?.detail || err.message); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <WarningIcon color="warning" />
        <Typography variant="h4">Дрейф изменений</Typography>
        {openCount > 0 && <Chip color="warning" size="small" label={`${openCount} в работе`} />}
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        Здесь видны <b>незарегистрированные изменения</b>: устройство поменяли напрямую, минуя заявку.
        Инженер обязан зайти и <b>указать причину</b>, после чего руководитель <b>подтверждает</b> —
        и состояние закрепляется как новая базовая линия. Критичные активы изменить напрямую вообще нельзя.
      </Alert>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map((t) => <Tab key={t.key} label={t.label} />)}
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="success">Дрейфа нет — всё под контролем.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Что</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Объект</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Тип</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Важность</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Когда</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((ev) => {
                const st = STATUS[ev.status] || { label: ev.status, color: 'default' };
                return (
                  <TableRow key={ev.id} hover>
                    <TableCell>{ev.id}</TableCell>
                    <TableCell sx={{ cursor: 'pointer', maxWidth: 360 }} onClick={() => openDetail(ev)}>{ev.summary}</TableCell>
                    <TableCell>{ev.entity_name || `${ev.entity_type} #${ev.entity_id}`}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={SOURCE_T[ev.drift_source] || ev.drift_source} /></TableCell>
                    <TableCell><Chip size="small" label={SEVERITY_T[ev.severity] || ev.severity} color={SEVERITY[ev.severity] || 'default'} /></TableCell>
                    <TableCell><Chip size="small" label={st.label} color={st.color} /></TableCell>
                    <TableCell>{new Date(ev.created_at).toLocaleString('ru-RU')}</TableCell>
                    <TableCell align="right">{rowActions(ev)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {detail && <DetailDialog ev={detail} onClose={() => setDetail(null)} />}
    </Box>
  );
}

function DetailDialog({ ev, onClose }) {
  const st = STATUS[ev.status] || { label: ev.status, color: 'default' };
  const diff = ev.diff_json && typeof ev.diff_json === 'object' && !Array.isArray(ev.diff_json) ? ev.diff_json : null;
  const isFieldDiff = diff && Object.values(diff).every((v) => v && typeof v === 'object' && ('old' in v || 'new' in v));
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Дрейф #{ev.id}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={st.label} color={st.color} />
          <Chip size="small" variant="outlined" label={SOURCE_T[ev.drift_source] || ev.drift_source} />
          <Chip size="small" variant="outlined" label={`важность: ${SEVERITY_T[ev.severity] || ev.severity}`} />
          <Chip size="small" variant="outlined" label={ev.entity_name || `${ev.entity_type} #${ev.entity_id}`} />
        </Stack>
        <Typography variant="body2" sx={{ mb: 2 }}>{ev.summary}</Typography>

        {isFieldDiff ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Поле</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Было</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Стало</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {Object.entries(diff).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell>{k}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{String(v.old ?? '—')}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{String(v.new ?? '—')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : ev.diff_json ? (
          <Box component="pre" sx={{ fontSize: 12, bgcolor: '#f8fafc', p: 1, borderRadius: 1, overflow: 'auto' }}>
            {JSON.stringify(ev.diff_json, null, 2)}
          </Box>
        ) : null}

        {ev.engineer_reason && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <b>Объяснение инженера{ev.assigned_engineer ? ` (${ev.assigned_engineer.full_name})` : ''}:</b> {ev.engineer_reason}
          </Alert>
        )}
        {ev.resolution && (
          <Alert severity={ev.status === 'dismissed' ? 'warning' : 'success'}>
            <b>Решение{ev.confirmed_by ? ` (${ev.confirmed_by.full_name})` : ''}:</b> {ev.resolution}
          </Alert>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Закрыть</Button></DialogActions>
    </Dialog>
  );
}

export default Drift;
